import { toFeet } from '../core/units.js';
import { CEILING, FLOOR } from '../sim/standing.js';
import { formatClock } from '../sim/clock.js';

/**
 * The backtick panel: all seven standings, where you are, and what the clock is
 * doing.
 *
 * Built once and mutated, never re-rendered from a template string. A panel
 * that rebuilds its own DOM every frame is how a debug overlay ends up costing
 * more than the game it is overlaying, and the standing bars would lose their
 * transition mid-animation every time.
 *
 * The seven bars are the point. The rivalry matrix is invisible from inside the
 * game — you feel it as "the kitchen hates me now" three shifts later — and
 * this is where you watch a single BEO push four numbers in two directions at
 * once.
 */
export function createDebugPanel({ parent, standing }) {
  const root = document.createElement('div');
  root.className = 'panel';
  root.hidden = true;

  const title = document.createElement('div');
  title.className = 'panel-title';
  title.textContent = 'STANDING';
  root.append(title);

  const rows = new Map();
  for (const department of standing.all()) {
    const row = document.createElement('div');
    row.className = 'panel-row';

    const name = document.createElement('span');
    name.className = 'panel-name';
    name.textContent = department.short;
    name.style.color = `#${department.colour.toString(16).padStart(6, '0')}`;

    const track = document.createElement('span');
    track.className = 'panel-track';
    const fill = document.createElement('i');
    // The zero mark, so a bar reads as "which side of neutral" at a glance
    // rather than as a percentage of something.
    const zero = document.createElement('u');
    track.append(fill, zero);

    const value = document.createElement('span');
    value.className = 'panel-value';

    const tier = document.createElement('span');
    tier.className = 'panel-tier';

    row.append(name, track, value, tier);
    root.append(row);
    rows.set(department.id, { fill, value, tier });
  }

  const foot = document.createElement('div');
  foot.className = 'panel-foot';
  root.append(foot);

  parent.append(root);

  let recent = new Map();

  standing.onChange((event) => {
    for (const change of event.changes) {
      if (change.delta) recent.set(change.department, { delta: change.delta, at: performance.now() });
    }
  });

  return {
    root,
    get visible() {
      return !root.hidden;
    },
    toggle() {
      root.hidden = !root.hidden;
      return !root.hidden;
    },

    update(context, now = performance.now()) {
      if (root.hidden) return;

      for (const department of standing.all()) {
        const row = rows.get(department.id);
        // Half the track is one side of neutral.
        const fraction = (department.value - FLOOR) / (CEILING - FLOOR);
        const negative = department.value < 0;
        row.fill.style.left = negative ? `${fraction * 100}%` : '50%';
        row.fill.style.width = `${Math.abs(fraction - 0.5) * 100}%`;
        row.fill.dataset.tone = department.tier.tone;

        const change = recent.get(department.id);
        const fresh = change && now - change.at < 3500;
        row.value.textContent = fresh
          ? `${Math.round(department.value)} (${change.delta > 0 ? '+' : ''}${change.delta.toFixed(1)})`
          : `${Math.round(department.value)}`;
        row.value.dataset.tone = fresh ? (change.delta > 0 ? 'good' : 'bad') : 'idle';
        row.tier.textContent = department.tier.label;
      }

      foot.textContent = [
        `x ${toFeet(context.position.x).toFixed(0)}ft  z ${toFeet(context.position.z).toFixed(0)}ft`,
        context.room ?? 'outside',
        context.zones.length ? context.zones.join('+') : 'none',
        `${formatClock(context.minutes)} @ ${context.scale}x`,
        `${context.fps.toFixed(0)} fps`,
      ].join('   ·   ');
    },
  };
}
